import type { CustomSEO, SEOPageType } from '@iheartjane/data-access';
import { SEO_PAGE_TYPES } from '@iheartjane/data-access';
import {
  Banner,
  AlertIcon,
  Flex,
  Tabs,
  useToast,
  Box,
} from '@iheartjane/reefer';
import isEmpty from 'lodash-es/isEmpty';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import type { BloomOperatorConfig } from '@jane/business-admin/types';
import { EventNames, businessPaths, track } from '@jane/business-admin/util';

import { ModalPageWrapper } from './components/ModalPageWrapper';
import { SeoFormProvider } from './providers/SeoFormProvider';
import { NAV_LINKS } from './seoTabs/defaultSeoConstants';
import { PageSeo } from './seoTabs/PageSeo';

import styles from './PremiumSeo.module.css';

export const PremiumSeo = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [activeSection, setActiveSection] = useState<SEOPageType>('featured');
  const [isLoading, setIsLoading] = useState(false);

  const bloomMenuQuery = useFetchCurrentBloomMenu();
  const { mutate: updateBloomMenu } = useUpdateBloomMenu(
    bloomMenuQuery.data?.id
  );

  const resolvedSeo = bloomMenuQuery.data?.custom_seo;
  const faviconUrl = bloomMenuQuery.data?.favicon_url;
  const seoDefaults = bloomMenuQuery.data?.custom_seo_defaults;

  const initialValues = useMemo<CustomSEO>(() => {
    const initialValues: CustomSEO = {};
    SEO_PAGE_TYPES.forEach((seoType) => {
      initialValues[seoType] = {
        title: resolvedSeo?.[seoType]?.title ?? '',
        description: resolvedSeo?.[seoType]?.description ?? '',
      };
    });
    return initialValues;
  }, [resolvedSeo]);

  const formMethods = useForm<CustomSEO>({
    defaultValues: initialValues,
    reValidateMode: 'onChange',
    mode: 'all',
  });
  const {
    control,
    formState: { errors },
    trigger,
  } = formMethods;
  const formValues = useWatch({ control });

  useEffect(() => {
    /**
     * Trigger validation when form values intially get set.
     * This handles the case where invalid SEO values were saved
     * before validation was implemented.
     */
    trigger();
  }, [JSON.stringify(formValues)]);

  const hasNonDefaultValues = useMemo(
    () =>
      SEO_PAGE_TYPES.some(
        (seoType) =>
          formValues[seoType]?.title !== seoDefaults?.[seoType]?.title ||
          formValues[seoType]?.description !==
            seoDefaults?.[seoType]?.description
      ),
    [formValues, seoDefaults]
  );

  const seoVariables = useMemo(() => {
    return {
      storeName: bloomMenuQuery.data?.stores[0]?.name,
      storeCity: bloomMenuQuery.data?.stores[0]?.city,
      storeStateAbbr: bloomMenuQuery.data?.stores[0]?.state,
      storeType: 'Pickup',
      storeFulfillment: bloomMenuQuery.data?.stores[0]?.recreational
        ? 'Recreational'
        : 'Medical',
      category: 'flower',
      menuUrl: bloomMenuQuery.data?.menu_url || '',
      specialName: 'Bogo great deal',
      rowName: 'row 1',
      productName: 'Green Chocolate',
      productLineage: 'Sativa',
      productCategory: 'Edible',
      productSubCategory: 'Chocolates',
      productBrand: "Jane's Bakery",
    };
  }, [bloomMenuQuery.data]);

  useEffect(() => {
    if (bloomMenuQuery.isSuccess && resolvedSeo) {
      formMethods.reset(initialValues);
    }
  }, [bloomMenuQuery.isSuccess, resolvedSeo, initialValues, formMethods.reset]);

  const publishSeoCustomization = (data: CustomSEO) => {
    setIsLoading(true);
    updateBloomMenu(
      {
        custom_seo: data,
      },
      {
        onError: () => {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
          setIsLoading(false);
        },
        onSuccess: async () => {
          toast.add({
            label: 'SEO customization updated!',
            variant: 'success',
          });

          const bloomMenuData = bloomMenuQuery.data as BloomOperatorConfig;
          track({
            event: EventNames.EditedSeoInformation,
            operator_id: bloomMenuData.operator.id,
            operator_name: bloomMenuData.operator.name,
            old_content: bloomMenuData.custom_seo,
            new_content: data,
          });

          setIsLoading(false);
          navigate(businessPaths.premium());
        },
      }
    );
  };

  const restoreDefaults = () => {
    SEO_PAGE_TYPES.forEach((seoType) => {
      formMethods.setValue(
        `${seoType}.title`,
        seoDefaults?.[seoType]?.title ?? '',
        { shouldDirty: true }
      );
      formMethods.setValue(
        `${seoType}.description`,
        seoDefaults?.[seoType]?.description ?? '',
        { shouldDirty: true }
      );
    });
  };

  const scrollToSection = (pageType: SEOPageType) => {
    setActiveSection(pageType);
    document
      .getElementById(pageType)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const errorMessage = useMemo(() => {
    if (isEmpty(errors)) return undefined;
    const sectionNames = Object.keys(errors).map(
      (key) => NAV_LINKS.find((link) => link.value === key)?.label
    );
    return `Please fix the errors in ${sectionNames.join(', ')} to publish.`;
  }, [JSON.stringify(errors)]);

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isLoading}
      modalOverflow="hidden"
      onSubmit={publishSeoCustomization}
      preview={{
        onClick: restoreDefaults,
        disable: isLoading || !hasNonDefaultValues,
        loading: isLoading,
        label: 'Restore defaults',
      }}
      title="SEO Customization"
    >
      {/* 120 = 80px (header) + 40px (padding) */}
      <Flex
        height="calc(100% - 120px)"
        gap={40}
        mt={40}
        justifyContent="center"
      >
        <nav
          aria-label="SEO customization navigation"
          className={styles['nav']}
        >
          <Tabs<SEOPageType>
            direction="vertical"
            onChange={scrollToSection}
            tabWidth="200px"
            value={activeSection}
          >
            {NAV_LINKS.map((item) => (
              <Tabs.Tab
                value={item.value}
                key={item.value}
                label={
                  errors[item.value] ? (
                    <Flex alignItems="center">
                      <AlertIcon mr={8} size="sm" color="error" />
                      {item.label}
                    </Flex>
                  ) : (
                    item.label
                  )
                }
              />
            ))}
          </Tabs>
        </nav>

        <Box
          className={styles['sections']}
          minHeight="0"
          overflowY="auto"
          pb={40}
          maxWidth="944px"
        >
          <SeoFormProvider>
            <Flex flexDirection="column" gap={24}>
              {errorMessage && (
                <Banner
                  variant="error"
                  label={errorMessage}
                  icon={<AlertIcon />}
                />
              )}

              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}`}
                pageType="featured"
                faviconUrl={faviconUrl}
                pageDescription="the homepage"
                seoVariables={seoVariables}
              />
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/${seoVariables['category']}`}
                pageType="category"
                faviconUrl={faviconUrl}
                pageDescription="category pages"
                seoVariables={seoVariables}
              />
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/deals/489/${
                  seoVariables['specialName']
                }`}
                pageType="deal"
                faviconUrl={faviconUrl}
                pageDescription="deal pages"
                seoVariables={seoVariables}
              />
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/shop-all`}
                pageType="shopAll"
                faviconUrl={faviconUrl}
                pageDescription="shop all pages"
                seoVariables={{
                  ...seoVariables,
                  category: 'shop all',
                }}
              />
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/customRow/81917/${
                  seoVariables['rowName']
                }`}
                pageType="customRow"
                faviconUrl={faviconUrl}
                pageDescription="custom row pages"
                seoVariables={seoVariables}
              />
              <PageSeo
                menuUrl={`${seoVariables['menuUrl']}/products/147331/foria-sativa-hawaiian-haze`}
                pageType="pdp"
                faviconUrl={faviconUrl}
                pageDescription="pdp pages"
                seoVariables={seoVariables}
              />
            </Flex>
          </SeoFormProvider>
        </Box>
      </Flex>
    </ModalPageWrapper>
  );
};
